import { IProfilingCode } from 'legacy/lut/classes/models/profiling/codes'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { Market, Region } from 'clickNgo/configs/geo'
import { IContactFlag } from 'clickNgo/entities/contactFlag'
import {
  fetchContactFlagProfilingCodes,
  filterAvailableContactFlags,
} from 'clickNgo/entities/contactFlag/api'
import { ProfilingCodeType } from 'clickNgo/entities/profilingCode'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'

import { handleContactFlagsAccessCondition } from '../helpers/handleContactFlagsAccessCondition'
import { handleFetchToolAccessConditions } from '../helpers/handleToolAccessConditions'

interface IUseGetAvailableFlagsParams {
  contactFlags: IContactFlag[]
  partnerId: string
  market: Market
  region: Region
  country: string
  type: ProfilingCodeType.UserRights | ProfilingCodeType.Tools | ProfilingCodeType.Campaigns
  isHQ: boolean
  skip?: boolean
}

export const useGetAvailableFlags = ({
  contactFlags,
  partnerId,
  market,
  region,
  country,
  type,
  isHQ,
  skip = false,
}: IUseGetAvailableFlagsParams) => {
  const [isLoading, setIsLoading] = useState(false)
  const [availableFlags, setAvailableFlags] = useState<IProfilingCode[]>([])
  const [profilingCodes, setProfilingCodes] = useState<IProfilingCode[]>([])

  const user = useSelector(selectUser)

  useEffect(() => {
    let isActual = true

    setIsLoading(!skip)
    fetchContactFlagProfilingCodes({ market, type, country })
      .then(async (profilingCodeList) => {
        if (!isActual) {
          return
        }

        setProfilingCodes(profilingCodeList)

        if (skip) {
          setAvailableFlags([])
          return
        }

        const filteredAvailableFlags = await filterAvailableContactFlags({
          profilingCodeList,
          contactFlags,
          type,
        })
        const availableFlagsByAccessCondition = handleContactFlagsAccessCondition({
          profilingCodeList: filteredAvailableFlags,
          email: user.email,
          roles: user.roleIds,
        })
        const availableFlagsByToolAccessConditions = await handleFetchToolAccessConditions({
          partnerId,
          profilingCodeList: availableFlagsByAccessCondition,
          type,
          isHQ,
        })

        if (isActual) {
          setAvailableFlags(availableFlagsByToolAccessConditions)
        }
      })
      .finally(() => {
        if (isActual) {
          setIsLoading(false)
        }
      })

    return () => {
      isActual = false
    }
  }, [isHQ, partnerId, user.email, user.roleIds, contactFlags, market, region, type, skip, country])

  return { availableFlags, profilingCodes, isLoading }
}
